import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useSignatureService } from '../../services/api/signatureService';
import { useSignatureClientService } from '../../services/api/signatureClientService';
import { getLocalClientStatus } from '../../services/localSignatureClient';
import {
  listTokens, signBatch, daemonErrorMessage, base64ToBytes,
  getAuthorizationStatus,
} from '../../services/signatureDaemonClient';
import { isBelowMinimum } from '../../utils/version';
import { colors, spacing, typography, radius } from '../../theme/tokens';
import { ENV } from '../../config/env';
import { SIGNATURE_CLIENT_MESSAGES as CLIENTE_MSG } from '../../constants/signatureClientMessages';

/** Mensajes para los {@code reason} de la validación server-side del confirm. */
const CONFIRM_REASON_MESSAGES = {
  user_mismatch: 'El certificado no corresponde a tu usuario.',
  untrusted: 'El certificado no encadena a una autoridad de confianza.',
  chain_untrusted: 'El certificado no encadena a una autoridad de confianza.',
  revoked: 'El certificado está revocado.',
  expired: 'El certificado está vencido.',
  coverage_incomplete: 'La firma no cubre todo el documento.',
  derivation_mismatch: 'El documento firmado no coincide con la versión a firmar.',
  signature_invalid: 'La firma no es criptográficamente válida.',
  // #108: la firma salió con menos garantías de las que la serie exige (típicamente, sin el sello de
  // tiempo). No se publica: reintentar sólo sirve si se resuelve lo que impidió sellar.
  NIVEL_INSUFICIENTE: 'La firma no alcanzó el nivel que exige la serie documental '
    + '(falta el sello de tiempo). Revisá la configuración del cliente de firma o avisá al administrador.',
  // Motivos de revocación. Sin estos, un rechazo por revocación caía en el mensaje genérico "la
  // validación falló", que no dice qué pasó ni qué hacer: había que leer los logs del servidor para
  // enterarse. La serie decide qué tan estricta es la política (OFF / LENIENT / STRICT).
  revocation_revoked: 'El certificado con el que firmaste está REVOCADO. No se puede firmar con él.',
  revocation_unknown: 'No se pudo verificar si el certificado está revocado, y esta serie documental '
    + 'exige una verificación concluyente. Suele pasar cuando el certificado está vencido o cuando el '
    + 'servidor no puede consultar a la autoridad certificante. Avisá al administrador.',
};

/**
 * Motivos que el daemon devuelve en {@code failures[]} de /sign-batch (#108). Al firmar de a uno,
 * el aislamiento por-digest se traduce en "la única firma del lote falló", así que el motivo llega
 * por acá en vez de por una excepción.
 */
const DAEMON_FAILURE_MESSAGES = {
  tsa_no_configurada: 'El cliente de firma no tiene configurado el servicio de sello de tiempo, '
    + 'que esta serie exige. Avisá al administrador.',
  nivel_no_soportado: 'El cliente de firma instalado no puede producir el nivel que exige la serie. '
    + 'Actualizá el cliente o avisá al administrador.',
};

const newIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * Modal de firma individual con el cliente local (#59, F1C, §1C.7 Slice B; motor unificado en #108).
 * Orquesta el flujo completo browser↔daemon↔API: status del daemon → PIN → /tokens (daemon,
 * enumera certs) → start (API: lock, lote de uno y digest a firmar) → /sign-batch (daemon, firma el
 * digest) → confirm (API: ensambla el CMS, validación triple + PUBLISHED). Cancelar o cerrar antes
 * de confirmar libera el lock (cancel).
 *
 * El documento no viaja en ninguna dirección: van y vienen un digest y un CMS. Es el mismo motor
 * que usa la firma masiva (ADR-030), que era el punto de #108.
 *
 * @param {boolean} open
 * @param {() => void} onClose — cierra sin éxito (el modal libera el lock si hace falta)
 * @param {() => void} onSigned — firma confirmada OK (el caller refresca la bandeja)
 * @param {{idDoc:number, filename?:string}} doc
 */
const SignDocumentModal = ({ open, onClose, onSigned, doc }) => {
  const signatureService = useSignatureService();
  const signatureClientService = useSignatureClientService();

  // 'checking' | 'daemon_down' | 'client_outdated' | 'awaiting_consent' | 'consent_timeout'
  //   | 'pin' | 'certs' | 'signing' | 'confirming'
  //   | 'success' | 'failed' | 'error' | 'expired'
  const [step, setStep] = useState('checking');
  const [message, setMessage] = useState('');
  const [pin, setPin] = useState('');
  const [certs, setCerts] = useState([]);
  const [certAlias, setCertAlias] = useState('0');

  const lockTokenRef = useRef(null);
  // #108: el documento ya no viaja. Del start vuelve el lote de uno y el digest a firmar.
  const batchIdRef = useRef(null);
  const digestRef = useRef(null);
  const signingDateRef = useRef(null);
  const idemKeyRef = useRef(null);
  const lockReleasedRef = useRef(false);
  const padesLevelRef = useRef(null);   // nivel PAdES de la Serie (del start, ADR-026/#77)
  const consentPollRef = useRef(null);  // timeout id del poll de consentimiento (I6, ADR-033)
  const mountedRef = useRef(true);      // false tras desmontaje: evita setState stale en polls (I6)

  // Limpiar el poll de consentimiento al desmontar el modal (I6, ADR-033): evita que un
  // setTimeout pendiente llame setStep después de que el componente fue desmontado.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (consentPollRef.current) { clearTimeout(consentPollRef.current); consentPollRef.current = null; }
    };
  }, []);

  const resetState = useCallback(() => {
    // Cancelar cualquier poll de consentimiento en vuelo antes de resetear (I6).
    if (consentPollRef.current) { clearTimeout(consentPollRef.current); consentPollRef.current = null; }
    setStep('checking');
    setMessage('');
    setPin('');
    setCerts([]);
    setCertAlias('0');
    lockTokenRef.current = null;
    batchIdRef.current = null;
    digestRef.current = null;
    signingDateRef.current = null;
    idemKeyRef.current = null;
    lockReleasedRef.current = false;
    padesLevelRef.current = null;
  }, []);

  // 1) Al abrir: chequear que el daemon esté corriendo, verificar consentimiento de origen
  //    (ADR-033) y pedir el PIN.
  //    El lock + la descarga de la versión NO se toman acá (lazy lock, #68): tomar el lock al
  //    abrir lo retenía durante toda la espera del PIN, y si el modal quedaba abierto más que el
  //    TTL del lock (30 min) el confirm fallaba con un mensaje genérico. Ahora el lock se toma
  //    recién tras validar el PIN (handleLoadCerts), minimizando la ventana en que se retiene.
  const prepare = useCallback(async () => {
    // Cancelar cualquier poll previo antes de iniciar una nueva secuencia (I6).
    if (consentPollRef.current) { clearTimeout(consentPollRef.current); consentPollRef.current = null; }
    setStep('checking');
    setMessage('');
    const status = await getLocalClientStatus();
    if (!mountedRef.current) return;
    if (!status) {
      setStep('daemon_down');
      return;
    }
    // #108: un cliente por debajo del mínimo ignora el nivel PAdES y firma sin sello — esa firma se
    // rechaza al publicarla, con el PIN ya gastado. Se corta antes. Best-effort: si el manifest no
    // responde, se sigue (el control del servidor sigue en pie).
    try {
      const manifest = await signatureClientService.getManifest();
      if (!mountedRef.current) return;
      if (isBelowMinimum(status.version, manifest?.minimumVersion)) {
        setStep('client_outdated');
        return;
      }
    } catch { /* manifest no disponible: no se bloquea por datos que no se tienen */ }
    // Gate de consentimiento por origen (ADR-033): antes del PIN, verificar autorización.
    let auth;
    try {
      auth = await getAuthorizationStatus();
    } catch {
      if (mountedRef.current) setStep('daemon_down');
      return;
    }
    if (!mountedRef.current) return;
    if (!auth.authorized) {
      setStep('awaiting_consent');
      const started = Date.now();
      const poll = async () => {
        if (!mountedRef.current) return;
        let a;
        try {
          a = await getAuthorizationStatus();
        } catch {
          if (mountedRef.current) setStep('daemon_down');
          return;
        }
        if (!mountedRef.current) return;
        if (a.authorized) { setStep('pin'); return; }
        if (Date.now() - started > 60000) { setStep('consent_timeout'); return; }
        consentPollRef.current = setTimeout(poll, 2000);
      };
      consentPollRef.current = setTimeout(poll, 2000);
      return;
    }
    setStep('pin');
  }, []);

  useEffect(() => {
    if (open && doc?.idDoc) {
      resetState();
      prepare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.idDoc]);

  // 2) PIN → enumerar certs del token (daemon) y RECIÉN AHÍ tomar el lock + descargar la versión
  //    congelada (lazy lock, #68). Si el PIN es inválido no se llega a tomar el lock.
  const handleLoadCerts = async () => {
    if (!pin) return;
    setStep('signing'); // spinner mientras abre el token
    setMessage('');
    // (a) PIN → certs del token.
    let tokens;
    try {
      tokens = await listTokens({ pin });
    } catch (e) {
      setMessage(daemonErrorMessage(e));
      setStep('pin');
      return;
    }
    if (!tokens.length) {
      setMessage('El token no tiene certificados disponibles.');
      setStep('pin');
      return;
    }
    // (b) Lazy lock: tomar el lock y preparar el placeholder. El start devuelve el digest del
    // ByteRange —no el documento— así que ya no hay una segunda llamada de descarga (#108). Con eso
    // se cierran de raíz los dos fallos que tenía este paso: el timeout de la descarga (#125) y el
    // 413 del daemon al mandarle un PDF de más de 1 MB.
    try {
      const startRes = await signatureService.startSignature(doc.idDoc);
      lockTokenRef.current = startRes?.data?.lockToken;
      batchIdRef.current = startRes?.data?.batchId;
      digestRef.current = startRes?.data?.messageDigestHex;
      signingDateRef.current = startRes?.data?.signingDate;
      padesLevelRef.current = startRes?.data?.padesLevel || null;   // nivel de la Serie (ADR-026/#77)
    } catch (e) {
      if (e?.response?.status === 409) {
        setMessage('El documento está siendo firmado por otro usuario.');
      } else {
        setMessage('No se pudo preparar la firma del documento en el servidor.');
      }
      setStep('error');
      return;
    }
    // Sin digest no hay nada que firmar. Cortar acá evita mandarle al token un pedido incompleto y
    // gastar el PIN para nada.
    if (!digestRef.current || !batchIdRef.current) {
      setMessage('El servidor no devolvió el documento a firmar. Reintentá; si persiste, avisá al administrador.');
      setStep('error');
      return;
    }
    setCerts(tokens);
    setCertAlias(tokens[0].alias);
    setStep('certs');
  };

  // 3) Firmar (daemon) + confirmar (API).
  //    #108: el token firma el digest con /sign-batch —el mismo camino que la firma masiva— y
  //    devuelve un CMS. El PDF nunca sale de la Bóveda.
  const handleSign = async () => {
    setStep('signing');
    setMessage('');
    let cmsBase64;
    try {
      const digest = {
        docId: doc.idDoc,
        messageDigestHex: digestRef.current,
        signingDate: signingDateRef.current,
      };
      // El daemon rechaza propiedades desconocidas con 500: la clave sólo va si hay valor, para que
      // un cliente viejo (sin padesLevel) siga funcionando contra este servidor.
      if (padesLevelRef.current) digest.padesLevel = padesLevelRef.current;

      const res = await signBatch({ digests: [digest], pin, certAlias });
      // Dos formas de respuesta: array plano (ADR-036) o {results, failures} (legacy).
      const results = Array.isArray(res) ? res : (res?.results || []);
      const failures = Array.isArray(res) ? [] : (res?.failures || []);
      cmsBase64 = results[0]?.cmsBase64;

      if (!cmsBase64) {
        // Aislamiento por-digest: con un solo documento, "el lote siguió" significa que la única
        // firma falló. El motivo viene en failures[], no como excepción.
        const reason = failures[0]?.error;
        setMessage(DAEMON_FAILURE_MESSAGES[reason]
          || (reason ? daemonErrorMessage({ reason })
            : 'El cliente de firma no devolvió la firma del documento.'));
        setStep('certs');
        return;
      }
    } catch (e) {
      setMessage(daemonErrorMessage(e));
      setStep('certs');
      return;
    }

    setStep('confirming');
    try {
      if (!idemKeyRef.current) idemKeyRef.current = newIdempotencyKey();
      const bytes = base64ToBytes(cmsBase64);
      const confirmRes = await signatureService.confirmSignature(doc.idDoc, bytes, {
        idempotencyKey: idemKeyRef.current,
        lockToken: lockTokenRef.current,
        batchId: batchIdRef.current,
      });
      const data = confirmRes?.data;
      if (data?.ok) {
        lockReleasedRef.current = true; // el server liberó el lock al publicar
        setStep('success');
      } else {
        lockReleasedRef.current = true; // el confirm fallido también libera el lock (D6)
        setMessage(CONFIRM_REASON_MESSAGES[data?.reason] || 'La validación de la firma falló.');
        setStep('failed');
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        // 409 = lock vencido/perdido (requireActiveLock server-side). Reintentar el confirm no
        // sirve: hay que reabrir el flujo para tomar un lock nuevo. El server ya no tiene el lock,
        // así que no intentamos cancelarlo al cerrar.
        lockReleasedRef.current = true;
        setMessage('La sesión de firma expiró o el documento se desbloqueó. '
          + 'Volvé a ingresar el PIN para firmar de nuevo.');
        setStep('expired');
      } else {
        setMessage('No se pudo confirmar la firma. Reintentá en unos segundos.');
        setStep('error');
      }
    }
  };

  // Cierre: si hay un lock tomado y no fue liberado por el server (éxito/fallo de validación),
  // liberarlo con cancel (best-effort) para no dejar el documento bloqueado.
  const handleClose = useCallback(() => {
    const lockToken = lockTokenRef.current;
    if (lockToken && !lockReleasedRef.current && doc?.idDoc) {
      signatureService.cancelSignature(doc.idDoc, lockToken).catch(() => {});
      lockReleasedRef.current = true;
    }
    onClose?.();
  }, [doc?.idDoc, signatureService, onClose]);

  const busy = step === 'signing' || step === 'confirming' || step === 'checking'
    || step === 'awaiting_consent';

  const title = step === 'success' ? 'Firma completada' : 'Firmar documento';

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : (step === 'success' ? onSigned : handleClose)}
      title={title}
      size="md"
      closeOnBackdrop={!busy}
      closeOnEsc={!busy}
      footer={renderFooter()}
    >
      <p style={{ margin: 0, marginBottom: spacing[3], color: colors.textMuted }}>
        {doc?.filename ? (
          <>Documento: <strong style={{ color: colors.text }}>{doc.filename}</strong></>
        ) : (
          <>Documento #{doc?.idDoc}</>
        )}
      </p>
      {renderBody()}
    </Modal>
  );

  function renderBody() {
    switch (step) {
      case 'checking':
        return <p style={styles.muted}>Verificando el cliente local de firma…</p>;
      case 'awaiting_consent':
        return (
          <div style={styles.alertWarn}>
            Autorizá este sitio en la ventana del cliente de firma para continuar.
          </div>
        );
      case 'consent_timeout':
        return (
          <div style={styles.alertWarn}>
            El tiempo para autorizar el sitio expiró. Abrí el cliente de firma, autorizá el sitio y reintentá.
          </div>
        );
      case 'daemon_down':
        return (
          <div style={styles.alertWarn}>
            {CLIENTE_MSG.daemonDown(ENV.APP_NAME)}
            {' '}
            <a
              href={CLIENTE_MSG.clientPagePath}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.alertLink}
            >
              {CLIENTE_MSG.clientPageCta}
            </a>
          </div>
        );
      case 'client_outdated':
        return (
          <div style={styles.alertError}>
            La versión instalada del cliente de firma es anterior a la mínima requerida y no puede
            firmar con las garantías que exigen las series documentales. Actualizalo desde la página
            del cliente de firma y volvé a intentar.
          </div>
        );
      case 'pin':
        return (
          <>
            {message && <div style={styles.alertError}>{message}</div>}
            <label htmlFor="sign-pin" style={styles.label}>PIN del token</label>
            <input
              id="sign-pin"
              type="password"
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLoadCerts(); }}
              placeholder="Ingresá el PIN de tu token"
              style={styles.input}
            />
          </>
        );
      case 'certs':
        return (
          <>
            {message && <div style={styles.alertError}>{message}</div>}
            <p style={{ ...styles.label, marginBottom: spacing[2] }}>Certificado para firmar</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[2] }}>
              {certs.map((c) => (
                <label key={c.alias} style={styles.certRow}>
                  <input
                    type="radio"
                    name="cert"
                    value={c.alias}
                    checked={certAlias === c.alias}
                    onChange={() => setCertAlias(c.alias)}
                  />
                  <span style={{ marginLeft: spacing[2] }}>
                    <span style={{ color: colors.text }}>{c.subject}</span>
                    <br />
                    <span style={styles.certMeta}>
                      Emisor: {c.issuer} · vence {c.notAfter ? new Date(c.notAfter).toLocaleDateString('es-AR') : '-'}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </>
        );
      case 'signing':
        return <p style={styles.muted}>Firmando con el token… no retires el dispositivo.</p>;
      case 'confirming':
        return <p style={styles.muted}>Validando y registrando la firma…</p>;
      case 'success':
        return <div style={styles.alertOk}>El documento se firmó y publicó correctamente. ✓</div>;
      case 'failed':
        return <div style={styles.alertError}>{message}</div>;
      case 'expired':
        return <div style={styles.alertWarn}>{message}</div>;
      case 'error':
      default:
        return <div style={styles.alertError}>{message || 'Ocurrió un error al firmar.'}</div>;
    }
  }

  function renderFooter() {
    if (step === 'success') {
      return <Button variant="primary" onClick={onSigned}>Listo</Button>;
    }
    if (step === 'client_outdated') {
      // Sin botón de reintentar: reintentar con el mismo cliente da exactamente lo mismo.
      return <Button variant="ghost" onClick={handleClose}>Cerrar</Button>;
    }
    if (step === 'daemon_down' || step === 'consent_timeout') {
      return (
        <>
          <Button variant="ghost" onClick={handleClose}>Cerrar</Button>
          <Button variant="primary" onClick={prepare}>Reintentar</Button>
        </>
      );
    }
    if (step === 'pin') {
      return (
        <>
          <Button variant="ghost" onClick={handleClose}>Cancelar</Button>
          <Button variant="primary" onClick={handleLoadCerts} disabled={!pin}>Continuar</Button>
        </>
      );
    }
    if (step === 'certs') {
      return (
        <>
          <Button variant="ghost" onClick={handleClose}>Cancelar</Button>
          <Button variant="primary" onClick={handleSign}>Firmar documento</Button>
        </>
      );
    }
    if (step === 'expired') {
      return (
        <>
          <Button variant="ghost" onClick={handleClose}>Cerrar</Button>
          <Button variant="primary" onClick={() => { resetState(); prepare(); }}>Reintentar</Button>
        </>
      );
    }
    if (step === 'failed' || step === 'error') {
      return <Button variant="ghost" onClick={handleClose}>Cerrar</Button>;
    }
    // checking / preparing / signing / confirming: sin acciones (en curso)
    return <Button variant="ghost" onClick={handleClose} disabled>Cancelar</Button>;
  }
};

const styles = {
  muted: { margin: 0, color: colors.textMuted },
  label: {
    display: 'block', marginBottom: spacing[1],
    fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: colors.text,
  },
  input: {
    width: '100%', boxSizing: 'border-box', padding: spacing[2],
    fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.sans, color: colors.text,
    border: `1px solid ${colors.borderStrong}`, borderRadius: radius.sm,
  },
  certRow: {
    display: 'flex', alignItems: 'flex-start', padding: spacing[2],
    border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer',
    fontSize: typography.fontSize.base,
  },
  certMeta: { fontSize: typography.fontSize.sm, color: colors.textMuted },
  alertError: {
    padding: spacing[2], marginBottom: spacing[3], borderRadius: radius.sm,
    backgroundColor: colors.errorBg, color: colors.errorDark, fontSize: typography.fontSize.sm,
  },
  alertWarn: {
    padding: spacing[3], borderRadius: radius.sm,
    backgroundColor: colors.warningBg, color: colors.warningDark, fontSize: typography.fontSize.base,
  },
  // El enlace se abre en otra pestaña a propósito: el usuario descarga e instala el cliente sin
  // perder el modal de firma, y vuelve a apretar "Reintentar".
  alertLink: { color: 'inherit', fontWeight: typography.fontWeight.semibold, textDecoration: 'underline' },
  alertOk: {
    padding: spacing[3], borderRadius: radius.sm,
    backgroundColor: colors.successBg, color: colors.successDark, fontSize: typography.fontSize.base,
  },
};

export default SignDocumentModal;
